/**
 * Subscription Management Page
 * 
 * Comprehensive subscription monetization interface:
 * 1. Subscription Status Card
 * 2. Plan Comparison Table
 * 3. Upgrade/Downgrade modals
 * 4. Subscription history
 * 
 * Goal: Drive upgrades while minimizing billing disputes and churn.
 */
import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClinicData } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import { 
  ArrowLeft, CreditCard, History, Settings, 
  Phone, Mail, ChevronRight, Users, Megaphone, Sparkles, Info
} from 'lucide-react';
import api from '../../utils/api';
import { formatCapacity, getResource } from '../../utils/quotaPresentation';

// Import subscription components
import SubscriptionStatusCard from '../../components/subscription/SubscriptionStatusCard';
import PlanComparisonTable from '../../components/subscription/PlanComparisonTable';
import UpgradeConfirmationModal from '../../components/subscription/UpgradeConfirmationModal';
import DowngradeModal from '../../components/subscription/DowngradeModal';
import SubscriptionBanner from '../../components/subscription/SubscriptionBanner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── TCPN PXC-B.1 — Provider Subscription Hero feature flag ───────────
// Gates the new canonical Provider Subscription hero surface. The legacy
// 2-column SubscriptionStatusCard + Quick Actions layout remains fully
// recoverable by flipping this flag back to `false` — no destructive
// replacement, no backend touch, no API touch, no commission engine
// touch. Flag default ON for PXC-B.1 ship.
const PXC_B1_PROVIDER_SUBSCRIPTION_HERO = true;

// Format BPS → percentage. The API returns commission_value in BPS
// (e.g. 1200 = 12.00%). NEVER calculate a fallback rate in the FE — if
// the API has not resolved a rule we render the empty state.
const _bpsToPct = (bps) => {
  if (typeof bps !== 'number' || !Number.isFinite(bps)) return null;
  return (bps / 100).toFixed(bps % 100 === 0 ? 0 : 1);
};

// Coerce a multilingual Pydantic Union[str, Dict[str,str]] field to a
// safe string. The Package.name / Package.description fields can arrive
// as either a plain string or a `{en, tr, de, es, ar, ru, fr}` dict.
const _i18nText = (value, locale = 'en') => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value[locale] || value.en || value[Object.keys(value)[0]] || '';
  }
  return String(value);
};

// ── TCPN PXC-B.1 — Provider Subscription Hero (presentational) ───────
// Renders the canonical Provider hero. All numeric values are sourced
// LIVE from the API:
//  • Plan name / price / renewal      → /api/subscription/clinic/{id}
//  • Capacity used / limits           → /api/clinic/quota-summary (entitlement)
//  • Marketplace lead commission      → /api/commission/preview?channel=marketplace&package_id=…
//  • Provider-owned patient commission → /api/commission/preview?channel=provider_owned&package_id=…
// NO FE-hardcoded percentages. NO FE-resolved rules. NO BPS exposed.
// NO priority/rule_id/resolution_chain exposed.
const _fmtMoney = (amt, ccy) => {
  if (typeof amt !== 'number') return '—';
  const symbol = ccy === 'EUR' ? '€' : ccy === 'USD' ? '$' : (ccy || '');
  return `${symbol}${amt.toFixed(0)}`;
};

// ZERO-PRICE-UPGRADE-01 — a published price of 0 is a VALID price. Only
// `null`/`undefined`/non-numeric means "no applicable published price".
const _hasPrice = (amt) => typeof amt === 'number' && Number.isFinite(amt) && amt >= 0;

const ProviderSubscriptionHero = ({
  packageKey,
  packageName,
  monthlyPrice,
  currency,
  renewalDate,
  status,
  entitlement,          // SSOT-ENTITLEMENT-PRESENTATION-01 (quota-summary)
  commission,           // {marketplace: {value_pct, loading, error}, provider_owned: {...}}
  onChangePlan,
}) => {
  const { t } = useTranslation();
  const _fmtDate = (d) => {
    if (!d) return null;
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return null; }
  };
  // Capacity is formatted ONLY from the backend entitlement contract:
  // unlimited → ∞, finite → used / limit, not configured → — (never 0).
  const _capacity = (key) => formatCapacity(getResource(entitlement, key));

  const renewalText = _fmtDate(renewalDate);
  const statusLabel = (status || '').toString().toUpperCase();
  const isActive = ['ACTIVE', 'TRIALING'].includes(statusLabel);

  const _commissionRow = (label, helper, channelData, testidPrefix) => (
    <div
      className="flex items-start justify-between gap-3 py-2 px-3 rounded-md border border-slate-200 bg-slate-50/60"
      data-testid={`${testidPrefix}-row`}
    >
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-slate-700">{label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{helper}</div>
      </div>
      <div className="shrink-0 text-right">
        {channelData?.loading ? (
          <span className="text-[11px] text-slate-400" data-testid={`${testidPrefix}-loading`}>…</span>
        ) : channelData?.value_pct ? (
          <span className="text-base font-semibold text-slate-900 tabular-nums" data-testid={`${testidPrefix}-value`}>
            {channelData.value_pct}%
          </span>
        ) : (
          <span className="text-[11px] text-slate-400" data-testid={`${testidPrefix}-empty`}>
            {t('provider.subscription.commission.notConfigured')}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="provider-subscription-hero" className="space-y-3">
      {/* ROW 1 — Provider plan banner */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2.5 rounded-lg bg-white border border-slate-200"
        data-testid="provider-hero-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium text-slate-500">{t('provider.subscription.banner.providerSubscription')}</span>
          <span className="text-slate-300">·</span>
          <span className="text-sm font-semibold text-slate-900 truncate" data-testid="provider-hero-plan-name">
            {packageName || packageKey || '—'}
          </span>
          {isActive && (
            <Badge variant="outline" className="ml-1 border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]" data-testid="provider-hero-status-badge">
              {t('provider.subscription.banner.active')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {renewalText && (
            <span className="text-[11px] text-slate-500" data-testid="provider-hero-renewal">
              Renews {renewalText}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={onChangePlan}
            data-testid="provider-hero-change-plan-btn"
          >
            Change plan
            <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
          </Button>
        </div>
      </div>

      {/* ROW 2 — three hero cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* CARD 1 — Current Provider Plan */}
        <Card className="border-slate-200 transition-all duration-200 hover:border-slate-300" data-testid="provider-hero-card-plan">
          <CardContent className="p-3.5 space-y-1">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Current provider plan
            </div>
            <div className="flex items-baseline gap-2">
              {!_hasPrice(monthlyPrice) ? (
                <span className="text-sm font-medium text-amber-600" data-testid="provider-hero-card-plan-price">
                  {t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}
                </span>
              ) : monthlyPrice === 0 ? (
                <span className="text-2xl font-bold text-slate-900" data-testid="provider-hero-card-plan-price">
                  {t('packageSelection.priceFree', 'Free')}
                </span>
              ) : (
                <>
                  <span className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="provider-hero-card-plan-price">
                    {_fmtMoney(monthlyPrice, currency)}
                  </span>
                  <span className="text-xs text-slate-500">/ month</span>
                </>
              )}
            </div>
            <div className="text-xs text-slate-700" data-testid="provider-hero-card-plan-tier">
              {packageName || packageKey || '—'}
            </div>
            {renewalText && (
              <div className="text-[11px] text-slate-500">
                Renews {renewalText}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CARD 2 — Capacity */}
        <Card className="border-slate-200 transition-all duration-200 hover:border-slate-300" data-testid="provider-hero-card-capacity">
          <CardContent className="p-3.5 space-y-1.5">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t('subscription.thisMonth', 'This month')}
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center justify-between" data-testid="provider-hero-capacity-included-users">
                <span className="text-slate-600 inline-flex items-center gap-1.5">
                  <Users className="w-3 h-3 text-slate-400" /> {t('subscription.includedUsers', 'Included users')}
                </span>
                <span className="font-medium text-slate-900 tabular-nums" data-testid="provider-hero-capacity-included-users-value">
                  {_capacity('included_users')}
                </span>
              </div>
              <div className="flex items-center justify-between" data-testid="provider-hero-capacity-marketplace-leads">
                <span className="text-slate-600 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-slate-400" /> {t('subscription.marketplaceLeads', 'Marketplace leads')}
                </span>
                <span className="font-medium text-slate-900 tabular-nums" data-testid="provider-hero-capacity-marketplace-leads-value">
                  {_capacity('leads')}
                </span>
              </div>
              <div className="flex items-center justify-between" data-testid="provider-hero-capacity-active-campaigns">
                <span className="text-slate-600 inline-flex items-center gap-1.5">
                  <Megaphone className="w-3 h-3 text-slate-400" /> {t('subscription.activeCampaigns', 'Active campaigns')}
                </span>
                <span className="font-medium text-slate-900 tabular-nums" data-testid="provider-hero-capacity-active-campaigns-value">
                  {_capacity('campaigns_active')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CARD 3 — Commercial terms (two-channel commission) */}
        <Card className="border-slate-200 transition-all duration-200 hover:border-slate-300" data-testid="provider-hero-card-commercial-terms">
          <CardContent className="p-3.5 space-y-1.5">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t('subscription.commercialTerms', { defaultValue: 'Commercial terms' })}
            </div>
            {_commissionRow(
              t('provider.subscription.commission.marketplaceLabel'),
              t('provider.subscription.commission.marketplaceHelper'),
              commission?.marketplace,
              'provider-hero-commission-marketplace'
            )}
            {_commissionRow(
              t('provider.subscription.commission.providerOwnedLabel'),
              t('provider.subscription.commission.providerOwnedHelper'),
              commission?.provider_owned,
              'provider-hero-commission-provider-owned'
            )}
            <div className="text-[10px] text-slate-400 pt-0.5 inline-flex items-start gap-1">
              <Info className="w-3 h-3 mt-px shrink-0" />
              <span>{t('subscription.liveRatesNote', { defaultValue: 'Live rates from your active provider plan. Existing bookings keep the rate they were sold at.' })}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const SubscriptionManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const resolvedClinicId = rbacClinicId || clinicUser?.clinicId;
  
  // State
  const [subscription, setSubscription] = useState(null);
  const [entitlement, setEntitlement] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Allow deep-links from legacy `/clinic/packages` / `/clinic/upgrade` redirects
  // to land directly on the Plans tab via `?tab=plans`.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = ['overview', 'plans', 'history'].includes(tabParam) ? tabParam : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep URL ?tab in sync with the active tab so refresh / share preserves state.
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      if (activeTab === 'overview') next.delete('tab');
      else next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab]); // eslint-disable-line
  
  // Modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [actionType, setActionType] = useState(null); // 'upgrade' | 'downgrade'
  
  // Banner dismissal state
  const [dismissedBanners, setDismissedBanners] = useState({});

  // ── PXC-B.1 — Live commission state (DB-driven, two-channel) ──────
  // Both channel rates are resolved server-side via /api/commission/preview.
  // Each entry: { value_pct: string|null, loading: bool, error: string|null }.
  // value_pct is null when the API has not resolved a rule — the hero
  // renders an explicit "Not configured" empty state in that case. We
  // NEVER fabricate a fallback percentage.
  const [commission, setCommission] = useState({
    marketplace: { value_pct: null, loading: true, error: null },
    provider_owned: { value_pct: null, loading: true, error: null },
  });
  
  // Fetch subscription and packages
  useEffect(() => {
    const fetchData = async () => {
      if (!resolvedClinicId) return;
      
      try {
        // Fetch current subscription
        const subResponse = await api.get(`/subscription/clinic/${resolvedClinicId}`);
        setSubscription(subResponse.data);
        
        // Fetch available packages
        const pkgResponse = await api.get('/subscription/packages');
        setPackages(pkgResponse.data.packages || []);

        // SSOT-ENTITLEMENT-PRESENTATION-01 — canonical effective entitlement
        // + actual usage for every quota rendered on this page.
        try {
          const quotaResponse = await api.get('/clinic/quota-summary');
          const block = quotaResponse?.data?.entitlement;
          setEntitlement(block && !block.error ? block : null);
        } catch (quotaError) {
          console.error('[SubscriptionPage] Entitlement unavailable:', quotaError);
          setEntitlement(null);
        }
      } catch (error) {
        console.error('[SubscriptionPage] Error fetching data:', error);
        toast({
          title: t('common.error', 'Error'),
          description: t('subscription.fetchError', 'Failed to load subscription data'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [clinicUser, t]);
  
  // Get current package details
  const currentPackageKey = subscription?.package_key || subscription?.packageId || 'TRIAL';
  const currentPackage = packages.find(p => p.id === currentPackageKey) || null;

  // ── PXC-B.1 — Resolve both commission channels live ─────────────────
  // Effect runs once we know the package_key. Issues TWO parallel
  // /api/commission/preview calls (marketplace + provider_owned). The
  // provider_id and country are passed when known — the engine falls
  // back to package-default → global default → safety. Empty-state on
  // any error or unresolved rule.
  useEffect(() => {
    if (!PXC_B1_PROVIDER_SUBSCRIPTION_HERO) return;
    if (!currentPackageKey) return;
    let cancelled = false;
    const fetchChannel = async (channel) => {
      try {
        const params = new URLSearchParams({
          channel,
          package_id: currentPackageKey,
          amount: '0',
        });
        if (resolvedClinicId) params.set('provider_id', resolvedClinicId);
        const country = clinicUser?.country || subscription?.country || '';
        if (country) params.set('country', country);
        const res = await api.get(`/commission/preview?${params.toString()}`);
        const data = res?.data || {};
        if (cancelled) return;
        const ok = data?.success && data?.commission_type === 'percentage' && typeof data?.commission_value === 'number';
        setCommission(prev => ({
          ...prev,
          [channel]: ok
            ? { value_pct: _bpsToPct(data.commission_value), loading: false, error: null }
            : { value_pct: null, loading: false, error: 'unresolved' },
        }));
      } catch (e) {
        if (cancelled) return;
        setCommission(prev => ({
          ...prev,
          [channel]: { value_pct: null, loading: false, error: e?.message || 'fetch_failed' },
        }));
      }
    };
    setCommission({
      marketplace: { value_pct: null, loading: true, error: null },
      provider_owned: { value_pct: null, loading: true, error: null },
    });
    fetchChannel('marketplace');
    fetchChannel('provider_owned');
    return () => { cancelled = true; };
  }, [currentPackageKey, resolvedClinicId, clinicUser?.country, subscription?.country]);

  // Handle plan selection
  const handleSelectPlan = (packageId, type, resolvedPkg) => {
    const pkg = packages.find(p => p.id === packageId);
    if (!pkg) return;

    // ZERO-PRICE-UPGRADE-03 — `packages` is the PUBLIC catalog
    // (`GET /subscription/packages`) and carries NO commercial price, so the
    // confirmation modal could not tell a free plan from a paid one and always
    // fell back to payment copy. The comparison table has already resolved the
    // CANONICAL price for this provider's jurisdiction, so carry it through
    // instead of dropping it. No second pricing authority is introduced.
    const selected = resolvedPkg
      ? {
          ...pkg,
          amount: resolvedPkg.amount ?? null,
          currency: resolvedPkg.currency ?? null,
          purchasable: resolvedPkg.purchasable,
        }
      : pkg;

    setSelectedPackage(selected);
    setActionType(type);
    
    if (type === 'upgrade') {
      setShowUpgradeModal(true);
    } else if (type === 'downgrade') {
      setShowDowngradeModal(true);
    }
  };
  
  // Handle contact sales — submit enterprise upgrade request
  const handleContactSales = async () => {
    if (!resolvedClinicId) return;
    try {
      await api.post('/subscription/enterprise-request', {
        clinic_id: resolvedClinicId,
        clinic_email: clinicUser?.email || '',
        clinic_name: clinicUser?.clinicName || '',
        message: t('provider.subscription.enterpriseInquiryMessage'),
      });
      toast({
        title: t('subscription.requestSubmitted', 'Request Submitted'),
        description: t('subscription.salesWillContact', 'Our sales team will contact you within 24 hours.'),
      });
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    }
  };
  
  // Handle cancel downgrade
  const handleCancelDowngrade = async () => {
    if (!resolvedClinicId) return;
    
    try {
      await api.delete(`/subscription/clinic/${resolvedClinicId}/downgrade`);
      
      toast({
        title: t('subscription.downgradeCanceled', 'Downgrade Canceled'),
        description: t('subscription.downgradeCanceledDesc', 'Your current plan will continue.'),
      });
      
      // Refresh subscription data
      setSubscription(prev => ({
        ...prev,
        pending_downgrade: null,
        pendingDowngrade: null,
      }));
    } catch (error) {
      console.error('[SubscriptionPage] Cancel downgrade error:', error);
      toast({
        title: t('common.error', 'Error'),
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    }
  };
  
  // Handle upgrade completion
  const handleUpgradeComplete = async (data) => {
    setShowUpgradeModal(false);
    // ZERO-PRICE-UPGRADE-01 — a zero-price upgrade is already active when the
    // modal resolves (no PSP redirect, so there is no return trip to refresh
    // on). Confirm it and re-read the canonical subscription.
    if (data?.activated) {
      toast({
        title: t('upgrade.activatedTitle', 'Plan Activated'),
        description: t('upgrade.activatedDesc', 'Your new plan is active. No payment was required.'),
      });
      if (resolvedClinicId) {
        try {
          const refreshed = await api.get(`/subscription/clinic/${resolvedClinicId}`);
          setSubscription(refreshed.data);
          setActiveTab('overview');
        } catch (err) {
          console.error('[SubscriptionPage] refresh after zero-price upgrade failed:', err);
        }
      }
    }
  };
  
  // Handle downgrade completion
  const handleDowngradeComplete = (data) => {
    setShowDowngradeModal(false);
    // Refresh subscription with pending downgrade
    setSubscription(prev => ({
      ...prev,
      pending_downgrade: {
        to_package_key: data.downgrade_to,
        to_package_name: data.downgrade_to_name,
        to_amount: data.new_amount,
      },
    }));
  };
  
  // Handle upgrade initiation
  const handleUpgrade = () => {
    setActiveTab('plans');
  };
  
  // TREATX-OWNER-UAT-SPRINT-02-P0-3 / SSOT-ENTITLEMENT-PRESENTATION-01 —
  // quotas are NEVER derived on the client. The provider dashboard reads the
  // clinic's EFFECTIVE entitlement and ACTUAL usage from the canonical
  // `entitlement` block of GET /api/clinic/quota-summary (resolved by
  // `EntitlementAuthority` — the same values the enforcement gates use).
  // The former `currentPackage?.limits` read (public catalogue) and the
  // `{leads_per_month: 50, sales_reps: 3, offers_per_month: 50}` local
  // fallback were a second authority and are removed.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Subscription Banners */}
      {subscription?.pending_downgrade && !dismissedBanners.downgrade && (
        <SubscriptionBanner 
          type="downgrade"
          pendingDowngrade={subscription.pending_downgrade}
          onCancelDowngrade={handleCancelDowngrade}
          onDismiss={() => setDismissedBanners(prev => ({ ...prev, downgrade: true }))}
        />
      )}
      
      {subscription?.status === 'TRIALING' && subscription?.trial_ends_at && !dismissedBanners.trial && (
        <SubscriptionBanner 
          type="trial_ending"
          trialEndsAt={subscription.trial_ends_at}
          onUpgrade={handleUpgrade}
          onDismiss={() => setDismissedBanners(prev => ({ ...prev, trial: true }))}
        />
      )}
      
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-4 min-w-0">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/clinic/admin')}
                className="text-gray-600"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.back', 'Back')}
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div>
                <h1 className="text-xl font-bold text-gray-900" data-testid="provider-subscription-page-title">
                  {PXC_B1_PROVIDER_SUBSCRIPTION_HERO
                    ? t('subscription.providerSubscription', 'Provider subscription')
                    : t('subscription.management', 'Subscription Management')}
                </h1>
                <p className="text-sm text-gray-500">
                  {clinicUser?.clinicName}
                </p>
              </div>
            </div>
            
            <Button 
              variant="outline"
              onClick={handleContactSales}
            >
              <Phone className="h-4 w-4 mr-2" />
              {t('subscription.contactSupport', 'Contact Support')}
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {t('subscription.overview', 'Overview')}
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {t('subscription.plans', 'Packages')}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              {t('subscription.history', 'History')}
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {PXC_B1_PROVIDER_SUBSCRIPTION_HERO ? (
              <>
                {/* TCPN PXC-B.1 — New canonical Provider Subscription
                    hero. Replaces the 2-column SubscriptionStatusCard +
                    Quick Actions + Next Billing layout. The legacy
                    components are NOT deleted — they continue to mount
                    when the feature flag is flipped to false. */}
                <ProviderSubscriptionHero
                  packageKey={currentPackageKey}
                  packageName={subscription?.package_name || _i18nText(currentPackage?.name) || currentPackageKey}
                  monthlyPrice={_hasPrice(subscription?.amount) ? subscription.amount : null}
                  currency={(subscription?.commercial_provenance?.currency || subscription?.currency || '').toString().toUpperCase()}
                  renewalDate={subscription?.next_billing_date}
                  status={subscription?.status}
                  entitlement={entitlement}
                  commission={commission}
                  onChangePlan={() => setActiveTab('plans')}
                />

                {/* Detail row — legacy quota detail kept (not deleted)
                    inside a single full-width card so usage drill-down
                    is still reachable. Quick Actions and Next Billing
                    duplicates are intentionally removed under this flag
                    (see PXC-B audit, section A.1 "Quick Actions row is
                    an admin-tool affordance"). */}
                <SubscriptionStatusCard 
                  subscription={subscription}
                  entitlement={entitlement}
                  onUpgrade={handleUpgrade}
                  onManageSubscription={() => setActiveTab('plans')}
                  onCancelDowngrade={subscription?.pending_downgrade ? handleCancelDowngrade : null}
                />
              </>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Subscription Status Card */}
                <div className="lg:col-span-2">
                  <SubscriptionStatusCard 
                    subscription={subscription}
                    entitlement={entitlement}
                    onUpgrade={handleUpgrade}
                    onManageSubscription={() => setActiveTab('plans')}
                    onCancelDowngrade={subscription?.pending_downgrade ? handleCancelDowngrade : null}
                  />
                </div>
                
                {/* Quick Actions */}
                <div className="space-y-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{t('subscription.quickActions', 'Quick Actions')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={() => setActiveTab('plans')}
                      >
                        {t('subscription.changePlan', 'Change Plan')}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={handleContactSales}
                      >
                        {t('subscription.contactSales', 'Contact Sales')}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={() => window.location.href = 'mailto:billing@treatx.health'}
                      >
                        <span className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {t('subscription.billingHelp', 'Billing Help')}
                        </span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                  
                  {/* Next Billing Info */}
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        {t('subscription.nextBilling', 'Next Billing')}
                      </h4>
                      <div className="flex items-baseline gap-1">
                        {!_hasPrice(subscription?.amount) ? (
                          <span className="text-sm font-medium text-amber-600">
                            {t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}
                          </span>
                        ) : subscription.amount === 0 ? (
                          <span className="text-2xl font-bold text-gray-900">
                            {t('packageSelection.priceFree', 'Free')}
                          </span>
                        ) : (
                          <>
                            <span className="text-2xl font-bold text-gray-900">
                              {_fmtMoney(subscription.amount, subscription.currency)}
                            </span>
                            <span className="text-gray-500">
                              /{subscription?.billing_interval || 'month'}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {subscription?.next_billing_date 
                          ? formatDateLocale(subscription.next_billing_date)
                          : t('subscription.notApplicable', 'N/A')
                        }
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>
          
          {/* Plans Tab */}
          <TabsContent value="plans">
            <Card>
              <CardHeader>
                <CardTitle>{t('subscription.comparePlans', 'Compare Packages')}</CardTitle>
                <CardDescription>
                  {t('subscription.chooseBestPlan', 'Choose the best package for your clinic\'s needs')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlanComparisonTable 
                  currentPackageKey={currentPackageKey}
                  currentTierRank={subscription?.plan_tier_rank ?? -1}
                  onSelectPlan={handleSelectPlan}
                  onContactSales={handleContactSales}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>{t('subscription.billingHistory', 'Billing History')}</CardTitle>
                <CardDescription>
                  {t('subscription.viewPastPayments', 'View your past payments and invoices')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>{t('subscription.noHistory', 'No billing history available')}</p>
                  <p className="text-sm mt-1">{t('subscription.historyWillAppear', 'Your invoices will appear here after your first payment')}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Upgrade Confirmation Modal */}
      <UpgradeConfirmationModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        fromPackage={currentPackage || { id: currentPackageKey, name: currentPackageKey }}
        toPackage={selectedPackage}
        onConfirm={handleUpgradeComplete}
      />
      
      {/* Downgrade Modal */}
      <DowngradeModal 
        isOpen={showDowngradeModal}
        onClose={() => setShowDowngradeModal(false)}
        fromPackage={currentPackage || { id: currentPackageKey, name: currentPackageKey }}
        toPackage={selectedPackage}
        onConfirm={handleDowngradeComplete}
      />
    </div>
  );
};

export default SubscriptionManagementPage;
